import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import components (lazy-loaded for performance)
const MediaCDNList = React.lazy(() => import("./MediaCDNList"));
const MediaCDNUpload = React.lazy(() => import("./MediaCDNUpload"));
const MediaCDNViewer = React.lazy(() => import("./MediaCDNViewer"));
const MediaCDNFolders = React.lazy(() => import("./MediaCDNFolders"));

/**
 * Media CDN Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (media-cdn/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/media-cdn/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const mediaCDNModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "media-cdn",
    name: "Media CDN",
    description: "File hosting and CDN for images, videos, documents, and more",
    version: "1.1.0",
    enabled: true,
    category: "tools",
    order: 20,
    icon: "\uD83D\uDCC1",
    color: "#3B82F6",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/media-cdn",
            component: MediaCDNList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/media-cdn/upload",
            component: MediaCDNUpload,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/media-cdn/folders",
            component: MediaCDNFolders,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/media-cdn/:id",
            component: MediaCDNViewer,
            permissionLevel: "view",
            useLayout: true,
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Media",
            path: "/media-cdn",
            icon: "\uD83D\uDCDA",
            permissionLevel: "view",
        },
        {
            label: "Upload",
            path: "/media-cdn/upload",
            icon: "\uD83D\uDCE4",
            permissionLevel: "create",
        },
        {
            label: "Folders",
            path: "/media-cdn/folders",
            icon: "\uD83D\uDCC1",
            permissionLevel: "view",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/media-cdn/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Upload File",
            path: "/media-cdn/upload",
            icon: "\uD83D\uDCE4",
            description: "Upload a new file to the CDN"
        },
        {
            label: "Browse Files",
            path: "/media-cdn",
            icon: "\uD83D\uDCDA",
            description: "Browse all uploaded files"
        },
        {
            label: "Manage Folders",
            path: "/media-cdn/folders",
            icon: "\uD83D\uDCC1",
            description: "Create and manage folders"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.1.0",
            releaseDate: "2025-01-04",
            title: "Edit & Thumbnails Update",
            features: [
                "Edit button for media items (name, description, tags)",
                "Video thumbnail generation from any frame",
                "CDN domain info card with copy functionality",
                "Storage & billing information banner",
                "Dark mode color improvements"
            ],
            notes: "Major update adding the ability to edit media item metadata after upload, generate thumbnails for videos, and improved UI with storage/billing info and CDN domain display."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-12-15",
            title: "Initial Release - Media CDN",
            features: [
                "Chunked file uploads for any file type",
                "IPFS-based storage with unique URLs",
                "Folder/collection organization",
                "Copy URL to clipboard",
                "File type detection and preview",
                "Search and filter by type, tags, folder",
                "Bulk delete operations",
                "Three-phase upload progress tracking"
            ],
            notes: "First release of the Media CDN module. Upload any file type (images, videos, documents, audio) and get unique URLs to embed anywhere."
        }
    ],

    // ROADMAP
    roadmap: [
        {
            title: "Image Optimization",
            description: "Automatic image resizing and format conversion",
            targetVersion: "1.2.0",
            priority: "high",
            status: "planned",
            features: [
                "Automatic thumbnail generation for images",
                "Multiple size variants",
                "WebP conversion",
                "Image compression"
            ]
        },
        {
            title: "Advanced Video Processing",
            description: "Video transcoding and streaming support",
            targetVersion: "1.3.0",
            priority: "medium",
            status: "planned",
            features: [
                "HLS streaming support",
                "Multiple quality variants",
                "Video preview player",
                "Auto-generate thumbnails on upload"
            ]
        }
    ]
};

export default mediaCDNModuleConfig;
